import React from "react";

interface MangoLogoProps {
  size?: number;
  showText?: boolean;
  className?: string;
  textClassName?: string;
}

export function MangoIcon({ size = 30, className = "" }: { size?: number; className?: string }) {
  const idPrefix = React.useId();
  const gradBody = `${idPrefix}-mango-body`;
  const gradLeaf = `${idPrefix}-mango-leaf`;
  const gradHighlight = `${idPrefix}-mango-highlight`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`shrink-0 transition-transform duration-300 group-hover:scale-105 group-hover:rotate-[-2deg] ${className}`}
      aria-hidden="true"
    >
      <defs>
        {/* Ripe juicy mango fruit gradient: amber gold to vibrant coral blush */}
        <linearGradient id={gradBody} x1="7" y1="8" x2="25" y2="28" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FBBF24" />
          <stop offset="35%" stopColor="#F97316" />
          <stop offset="78%" stopColor="#EA580C" />
          <stop offset="100%" stopColor="#E11D48" />
        </linearGradient>

        {/* Crisp botanical leaf gradient */}
        <linearGradient id={gradLeaf} x1="15" y1="3" x2="25.5" y2="10" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#34D399" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>

        {/* Tactile soft sheen on the fruit shoulder */}
        <linearGradient id={gradHighlight} x1="8.5" y1="11" x2="16" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        {/* Gentle organic depth shadow */}
        <filter id={`${idPrefix}-shadow`} x="2" y="3" width="28" height="28" filterUnits="userSpaceOnUse">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.8" floodColor="#9A3412" floodOpacity="0.28" />
        </filter>
      </defs>

      {/* Fresh angled green leaf */}
      <path
        d="M15.5 8 C17.2 3.8 22.8 2.6 25.5 3.6 C26 7.6 22.2 10.6 17.8 9.6 C16.6 9.4 15.8 8.8 15.5 8 Z"
        fill={`url(#${gradLeaf})`}
      />
      {/* Leaf subtle vein */}
      <path
        d="M17 7.5 C19.5 6.2 22.5 5.2 24.5 4.6"
        stroke="#A7F3D0"
        strokeWidth="0.8"
        strokeLinecap="round"
        strokeOpacity="0.85"
      />

      {/* Little organic twig / stem */}
      <path
        d="M15.2 8.8 C14.6 7 15 5.6 15.8 4.8"
        stroke="#78350F"
        strokeWidth="1.3"
        strokeLinecap="round"
      />

      {/* Plump, iconic asymmetrical Mango body */}
      <path
        d="M15.2 8.5
           C19.8 8.5 24.5 11.8 24.5 17.2
           C24.5 22.8 20.4 27.2 15.8 27.5
           C10.2 27.8 6.5 23.4 6.5 17.6
           C6.5 11.8 10.6 8.5 15.2 8.5 Z"
        fill={`url(#${gradBody})`}
        filter={`url(#${idPrefix}-shadow)`}
      />

      {/* Soft natural highlight curve */}
      <path
        d="M9.8 13.5 C10.6 11 12.8 9.8 15 9.5 C12.8 10.5 11.2 12.8 10.8 16 C10.6 17.5 10.8 19 11.2 20.2 C10.3 18.5 9.6 15.8 9.8 13.5 Z"
        fill={`url(#${gradHighlight})`}
      />

      {/* Subtle vocabulary card mark (two gentle micro notches representing study pages) */}
      <circle cx="18.5" cy="15.5" r="0.9" fill="#FFFFFF" fillOpacity="0.55" />
      <circle cx="18.5" cy="18.5" r="0.9" fill="#FFFFFF" fillOpacity="0.55" />
    </svg>
  );
}

export default function MangoLogo({
  size = 30,
  showText = true,
  className = "",
  textClassName = "",
}: MangoLogoProps) {
  return (
    <div className={`flex items-center gap-2.5 select-none ${className}`}>
      <MangoIcon size={size} />
      {showText && (
        <div className={`flex items-center font-grotesk tracking-tight leading-none ${textClassName}`}>
          <span className="font-bold text-[19px] text-current">Mango</span>
          <span className="font-medium text-[19px] text-current/80 ml-1">Word</span>
        </div>
      )}
    </div>
  );
}
